import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { KTSVG, toPercentage, valueHandler } from '../../../../../../_metronic/helpers';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import ErrorTooltip from '../../../../../modules/components/tooltip/ErrorTooltip';
import ReviewCard from '../card/ReviewCard';
import { getTranslatedValueWithFallback } from '../utils';

interface Props {
  data: any;
  categoryData: any;
  status: string;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
  isEditable?: boolean;
}

export default function CustomerDisputeDetail({ data, categoryData, status, translation, currentLanguage, refetch, isEditable }: Props) {
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_CUSTOMER_DISPUTE_DETAIL.${id}` }, values);

  const maxPreviewCount = 5;
  const emDash = '—';

  const categoryResults = categoryData;
  const fraudRecord = categoryResults?.result?.fraud_record ?? {};
  const fraudDetectedValue = fraudRecord?.fraud_detected;
  const hasFraudDetected = typeof fraudDetectedValue === 'boolean';
  const isFraudDetected = fraudDetectedValue === true;
  const complaintReason =
    typeof fraudRecord?.reason === 'string' && fraudRecord.reason.trim().length > 0
      ? getTranslatedValueWithFallback(
        translation,
        currentLanguage,
        'fraud_record.reason',
        fraudRecord.reason.trim()
      )
      : emDash;
  const shouldShowFraudRecord = Boolean(categoryResults?.result?.fraud_record) && hasFraudDetected;
  const [showDetail, setShowDetail] = useState(false);
  const [showAllComplaints, setShowAllComplaints] = useState(false);

  enum WordNumber {
    ONE = 1,
    TWO = 2,
    THREE = 3,
    FOUR = 4,
    FIVE = 5,
  }
  const maxRating = 5;
  const renderRatingStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < rating; i++) {
      stars.push(<i className='bi bi-star-fill text-warning'></i>);
    }
    for (let i = 0; i < maxRating - rating; i++) {
      stars.push(<i className='bi bi-star text-warning'></i>);
    }
    return stars;
  };

  const totalReviewCounts = useMemo(() => {
    const sources = data?.result?.sources || [];
    const res = {
      total: 0,
      complaint: 0,
      ratings: {
        [WordNumber.ONE]: 0,
        [WordNumber.TWO]: 0,
        [WordNumber.THREE]: 0,
        [WordNumber.FOUR]: 0,
        [WordNumber.FIVE]: 0,
      },
    };

    if (sources.length === 0 || sources[0]?.status === 'SKIPPED') return res;
    for (let source of sources) {
      res.total += source?.total_review_count ?? 0;
      res.ratings[WordNumber.ONE] += source?.rating_review_count?.one_star ?? 0;
      res.ratings[WordNumber.TWO] += source?.rating_review_count?.two_stars ?? 0;
      res.ratings[WordNumber.THREE] += source?.rating_review_count?.three_stars ?? 0;
      res.ratings[WordNumber.FOUR] += source?.rating_review_count?.four_stars ?? 0;
      res.ratings[WordNumber.FIVE] += source?.rating_review_count?.five_stars ?? 0;
    }
    res.complaint =
      res.ratings[WordNumber.THREE] + res.ratings[WordNumber.TWO] + res.ratings[WordNumber.ONE];
    return res;
  }, [data]);

  const hasSourceData =
    data?.result?.sources?.length > 0 &&
    data.result.sources[0]?.status !== 'SKIPPED' &&
    totalReviewCounts.total > 0;

  const getSourceDataSection = (sourceIndex: number, sourceData: any): SummaryRow[] => [
    {
      key: 'Entity',
      field: t('ENTITY'),
      render: () => <>{valueHandler(sourceData?.entity)}</>,
      canHaveRemark: isEditable,
      remarkKey: `sources[${sourceIndex}].entity`,
    },
    {
      key: 'TotalReviewCount',
      field: t('TOTAL_REVIEW_COUNT'),
      render: () => <>{valueHandler(sourceData?.total_review_count?.toLocaleString())}</>,
      canHaveRemark: isEditable,
      remarkKey: `sources[${sourceIndex}].total_review_count`,
    },
    {
      key: 'ReviewCountByRating',
      field: t('REVIEW_COUNT_BY_RATING'),
      render: () => {
        const ratings = Object.keys(sourceData?.rating_review_count ?? {});
        return (
          <>
            {ratings.length > 0
              ? ratings.map((key, index) => (
                <div key={index} className='d-flex gap-1'>
                  {renderRatingStars(WordNumber[key.split('_')[0].toLocaleUpperCase()])}
                  &nbsp;
                  {sourceData.rating_review_count[key]?.toLocaleString()}
                </div>
              ))
              : emDash}
          </>
        );
      },
      canHaveRemark: isEditable,
      remarkKey: `sources[${sourceIndex}].rating_review_count`,
    },
  ];

  return (
    <div className='card mb-5 mb-xl-10 border'>
      <div className='card-header align-items-center justify-content-start'>
        <h4 className='my-6'>{t('TITLE')}</h4>
      </div>
      <div className='card-body p-9 d-flex flex-column gap-9'>
        {shouldShowFraudRecord && <div className='border border-gray-300 rounded-4 p-6'>
          <div className='d-flex align-items-center gap-2 mb-2'>
            <i
              className={
                isFraudDetected
                  ? 'bi bi-exclamation-octagon-fill text-danger fs-4'
                  : 'bi bi-check-square-fill text-success fs-4'
              }
            ></i>
            <span
              className={isFraudDetected ? 'text-danger fw-bolder text-uppercase' : 'text-success fw-bolder text-uppercase'}
            >
              {isFraudDetected ? t('HIGH_RISK_FRAUD_ALERT_FLAG') : t('VERIFIED_CLEAR_RECORD')}
            </span>
          </div>

          <div className='text-gray-700 fs-6 lh-lg'>
            {complaintReason}
          </div>
        </div>}

        {hasSourceData ? (
          <>
            {/* Total Counts */}
            <div className='card card-body border d-flex flex-column flex-md-row gap-6 gap-md-9 p-6'>
              <div className='d-flex flex-column text-center justify-content-between mx-xl-8'>
                <div>
                  <div className='fw-semibold text-muted'>{t('TOTAL_REVIEWS')}</div>
                  <div className='fw-semibold fs-2x'>
                    {totalReviewCounts.total.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className='fw-semibold text-muted'>{t('TOTAL_COMPLAINTS')}</div>
                  <div className='fw-semibold fs-2x'>
                    {totalReviewCounts.complaint.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Ratings */}
              <div className='vstack gap-1'>
                {Object.entries(totalReviewCounts.ratings)
                  .sort((a, b) => +b[0] - +a[0]) // sort key by desc
                  .map(([key, value]) => (
                    <div className='d-flex align-items-center gap-2' key={key}>
                      <div className='d-flex align-items-center gap-1'>
                        <span className='text-center' style={{ width: '1ch' }}>
                          {key}
                        </span>
                        <div className='rating-label checked'>
                          <KTSVG
                            path='/media/icons/duotune/general/gen029.svg'
                            className='svg-icon svg-icon-1'
                          />
                        </div>
                      </div>
                      <div className='progress bg-light-dark h-10px w-100'>
                        <div
                          className='progress-bar'
                          role='progressbar'
                          style={{
                            width: `${toPercentage(value, totalReviewCounts.total)}%`,
                          }}
                          aria-valuenow={(value / totalReviewCounts.total) * 100}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        ></div>
                      </div>
                      <span className='text-muted text-end' style={{ width: '4ch' }}>
                        {value > 0 ? toPercentage(value, totalReviewCounts.total, 0) : 0}%
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Source Details */}
            {/* <div className='vstack gap-2'>
              <div>
                <button className='btn p-0' onClick={() => setShowDetail(!showDetail)}>
                  <h4 className='text-primary m-0'>{t('SOURCE_DETAILS')}</h4>
                </button>
              </div>
              <div className={showDetail ? 'd-flex flex-column gap-6' : 'd-none'}>
                {showDetail &&
                  data.result.sources.map((value, index) => (
                    <>
                      <SummaryTable
                        key={index}
                        rows={getSourceDataSection(index, value)}
                        data={data}
                        refetch={refetch}
                        className='m-0'
                        title={
                          <h5 className='m-0 d-flex align-items-center'>
                            {valueHandler(value.source)}
                            {value?.error_message && (
                              <ErrorTooltip
                                id={`${value.source}_error_tooltip`}
                                placement='top'
                                className='px-2'
                              >
                                <p className='m-0'>
                                  {value?.error_message ?? t('AN_ERROR_OCCURRED')}
                                </p>
                              </ErrorTooltip>
                            )}
                          </h5>
                        }
                      />
                    </>
                  ))}
              </div>
            </div> */}

            {/* Review Details */}
            <div>
              <h5 className='fw-bolder'>{t('LATEST_COMPLAINTS')}</h5>
              <div className='vstack gap-4'>
                {data?.detail?.length > 0 ? (
                  data.detail.slice(0, maxPreviewCount).map((item, index) => (
                    <ReviewCard
                      key={index}
                      source={item?.source} // TODO: hide this when client view
                      data={{
                        rating: item?.review_data?.rating,
                        title: item?.review_data?.title,
                        name: item?.reviewer_data?.name,
                        publishDate: item?.review_data?.published_at,
                        description: item?.review_data?.description,
                        location: item?.reviewer_data?.location,
                        experienceDate: item?.review_data?.date_of_experience,
                      }}
                    />
                  ))
                ) : (
                  <div>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })}</div>
                )}

                {showAllComplaints &&
                  data.detail.slice(maxPreviewCount).map((item, index) => (
                    <ReviewCard
                      key={index}
                      source={item?.source} // TODO: hide this when client view
                      data={{
                        rating: item?.review_data?.rating,
                        title: item?.review_data?.title,
                        name: item?.reviewer_data?.name,
                        publishDate: item?.review_data?.published_at,
                        description: item?.review_data?.description,
                        location: item?.reviewer_data?.location,
                        experienceDate: item?.review_data?.date_of_experience,
                      }}
                    />
                  ))}

                {data?.detail?.length > maxPreviewCount && (
                  <div>
                    <button
                      className='btn btn-link btn-color-muted btn-active-color-primary btn-sm p-0'
                      onClick={(e) => {
                        e.currentTarget.blur();
                        setShowAllComplaints(!showAllComplaints);
                      }}
                    >
                      {showAllComplaints ? (
                        <>
                          {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                        </>
                      ) : (
                        <>
                          {t('SHOW_ALL_MORE', { remaining: data.detail.length - maxPreviewCount })}
                          <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : !shouldShowFraudRecord ? (
          <div className='text-center'>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })} </div>
        ) : null}
      </div>
      <div className='d-flex justify-content-end mb-5'>
        <a href='#summary' className='px-8 d-none print-display-button'>
          <i className="bi bi-caret-up-square-fill fs-3"></i>
        </a>
      </div>
    </div>
  );
}
